<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';

import SchoolJoinExplanation from '~/components/Core/ClusterTerms/SchoolJoinExplanation.vue';
import TermsAcceptanceCard from '~/components/Core/ClusterTerms/TermsAcceptanceCard.vue';
import { JOIN_SCHOOL_FROM_INVITE, studentRoleInviteFragment, studentRoleInvitesSelfQuery } from '~/graphql/documents/core';
import { useFragment } from '~/graphql/generated/fragment-masking';
import { useAuthStore } from '~/stores/auth';
import { getFirstErrorMessageFromCombinedErrors } from '~/utils/graphql';

interface Props {
  /**
   * True when this page session opened a new account, so there is no existing profile to mention.
   * An invite that adopted an account the email already held leaves it false.
   */
  signedUp?: boolean;
}

withDefaults(defineProps<Props>(), { signedUp: false });
const emit = defineEmits<{ done: [] }>();

const { t } = useI18n();
const client = useUrqlClient();
const analytics = useAnalyticsService();
const { $logErrorMessage } = useNuxtApp();
const { logout } = useAuth();
const authStore = useAuthStore();

const { data, fetching, error, executeQuery } = useQuery({
  query: studentRoleInvitesSelfQuery,
  variables: {},
  requestPolicy: 'network-only',
  pause: computed(() => authStore.isloggingOut),
});

watch(error, (value) => {
  if (value) {
    $logErrorMessage('Failed to load student role invites', { error: value.message });
  }
}, { immediate: true });

const refetchInvites = () => executeQuery({ requestPolicy: 'network-only' });

const invites = computed(() => useFragment(studentRoleInviteFragment, data.value?.core.user.self?.studentRoleInvites ?? [])
  .flatMap(invite => (invite.school ? [{ ...invite, school: invite.school }] : [])));

// A list entry earns the " - {City}" suffix `label` carries: it disambiguates same-named
// schools. The prose below uses `displayName`, which drops the suffix but keeps the marker.
const schoolOptions = computed(() => invites.value.map(invite => ({ value: invite.id, label: invite.school.label })));

const draftInviteId = ref<string | null>(null);
const confirmedInviteId = ref<string | null>(null);

// A single invite skips the picker entirely — straight to its terms.
watch(invites, (value) => {
  if (value.length === 1) {
    draftInviteId.value = value[0]!.id;
    confirmedInviteId.value = value[0]!.id;
  }
}, { immediate: true });

const selectedInvite = computed(() => invites.value.find(invite => invite.id === confirmedInviteId.value) ?? null);
const selectedSchoolName = computed(() => selectedInvite.value?.school.displayName ?? '');

const confirmSelection = () => {
  if (typeof draftInviteId.value === 'string') {
    confirmedInviteId.value = draftInviteId.value;
  }
};

const joining = ref(false);
const joinError = ref<string | null>(null);

// A single "accept" runs one atomic mutation that records the cluster-terms acceptance (enrolment),
// the per-school terms acceptance, and accepts the invite (creating the school role) — server-side,
// in one transaction. Idempotent, so a retry after a failure is safe.
const joinSchool = async () => {
  const invite = selectedInvite.value;
  if (!invite) {
    return;
  }

  joining.value = true;
  joinError.value = null;

  const result = await client.mutation(JOIN_SCHOOL_FROM_INVITE, { studentRoleInviteId: invite.id });

  if (result.error || !result.data?.joinSchoolFromInvite.success) {
    joining.value = false;
    $logErrorMessage('Failed to join school from invite', { error: result.error?.message, inviteId: invite.id });
    const fallback = t('unified.auth.school.acceptError');
    joinError.value = result.error ? getFirstErrorMessageFromCombinedErrors(t, result.error, fallback) : fallback;
    return;
  }

  // Refresh the cached self user so the enrolment + new role resolve for later route navigation
  // (the cluster-terms route guard would otherwise still see the pre-join state).
  await authStore.fetchUser(true);

  analytics.track(EVENTS.ACCOUNT_SCHOOLS_UPDATE, { type: 'inviteAccepted' });
  analytics.track(EVENTS.INVITE_ACCEPTED);

  joining.value = false;
  emit('done');
};
</script>

<template>
  <div>
    <KUICoreContainerCentered v-if="fetching || authStore.isloggingOut">
      <KUICoreLoaderAnyway class="m-8" />
    </KUICoreContainerCentered>

    <div
      v-else-if="error"
      class="flex flex-col items-center gap-4"
      data-testid="school-step-error"
    >
      <Text
        size="md"
        class="text-center text-destructive"
      >
        {{ t('unified.auth.school.loadError') }}
      </Text>
      <Button
        size="default"
        type="button"
        @click="refetchInvites"
      >
        {{ t('unified.auth.school.retry') }}
      </Button>
    </div>

    <div
      v-else-if="invites.length === 0"
      class="flex flex-col items-center gap-6"
      data-testid="school-step-empty"
    >
      <div>
        <Heading
          size="xl"
          as="h1"
          class="text-center mb-4"
        >
          {{ t('unified.auth.school.noInvitesTitle') }}
        </Heading>
        <Text
          size="md"
          class="text-center"
        >
          {{ t('unified.auth.school.noInvites') }}
        </Text>
      </div>
      <!-- The student is authenticated but role-less; without an escape the resume watch re-parks
           them here on every /login (the guest layout has no logout). Give them a way out. -->
      <Button
        variant="secondary"
        size="default"
        data-testid="school-step-logout"
        type="button"
        @click="logout()"
      >
        {{ t('unified.auth.school.logout') }}
      </Button>
    </div>

    <div v-else-if="!selectedInvite">
      <Heading
        size="xl"
        as="h1"
        class="text-center mb-8"
      >
        {{ t('unified.auth.school.pickTitle') }}
      </Heading>

      <div class="flex flex-col gap-6">
        <KUICoreInputComboboxSelect
          v-model="draftInviteId"
          :label="t('unified.auth.school.pickTitle')"
          hide-label
          :placeholder="t('unified.auth.school.placeholder')"
          :options="schoolOptions"
          data-test-id="school-picker"
          version="v2"
        />
        <Button
          size="default"
          :disabled="!draftInviteId"
          type="button"
          class="w-full"
          @click="confirmSelection"
        >
          {{ t('unified.auth.school.continue') }}
        </Button>
      </div>
    </div>

    <TermsAcceptanceCard
      v-else
      testid="school-terms-step"
      accept-testid="accept-school-terms"
      :title="t('schools.schoolTermsStep.title', { schoolName: selectedSchoolName })"
      :description="t('schools.schoolTermsStep.description', { schoolName: selectedSchoolName })"
      :terms-text="t('schools.schoolTermsStep.termsLink')"
      :terms-href="clusterUrl('/terms')"
      :privacy-text="t('schools.schoolTermsStep.privacyLink')"
      :privacy-href="clusterUrl('/privacy-policy')"
      :accept-text="joining ? t('schools.schoolTermsStep.accepting') : t('schools.schoolTermsStep.accept')"
      :accepting="joining"
      :error="joinError"
      @accept="joinSchool"
    >
      <SchoolJoinExplanation :show-privacy-link="false" :show-shared-profile="!signedUp" />
    </TermsAcceptanceCard>
  </div>
</template>
